import { BLOCKS } from '@contentful/rich-text-types';
import type { AnyObject } from 'antd/es/_util/type';
import type { HTMLAttributes } from 'react';
import { z } from 'zod';
import { CmsImageSchema, CmsNumberSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import { ReviewProvider } from '$constants/review-providers';
import { makeContentComponentDefaultConfig } from '$util/makeContentComponentDefaultConfig';
import type { SchemaType } from '$util/schema';

export const defaultConfiguration = makeContentComponentDefaultConfig({
  reviewsProvider: ReviewProvider.TRUSTPILOT,
  sku: undefined,
});

export interface PageTypeProps {
  readonly slug: string;
  readonly sku?: string;
  readonly type: string;
}

export interface HeroSectionProps extends HTMLAttributes<HTMLDivElement> {
  readonly pageType?: PageTypeProps[];
  readonly extraParams?: AnyObject;
  readonly targetBlank?: boolean;
  readonly formAction?: string;
  readonly topComponent?: boolean;
}

export const HeroSectionSchema = {
  translatedEntities: z.object({
    heading: z.union([CmsRichTextSchema, CmsShortTextSchema]),
    newHeroHeading: z.union([CmsRichTextSchema, CmsShortTextSchema]).optional(),
    subheading: CmsShortTextSchema.optional(),
    postcodeLabel: CmsShortTextSchema,
    vrmLabel: CmsShortTextSchema,
    mainCta: CmsShortTextSchema,
    invalidPostcodeMessage: CmsShortTextSchema,
    invalidVRMMessage: CmsShortTextSchema,
    missingPostcodeMessage: CmsShortTextSchema,
    missingVRMMessage: CmsShortTextSchema,
    price: CmsShortTextSchema.optional(),
    speechBubbleOne: CmsShortTextSchema.default({
      key: 'HeroSection.speechBubbleOne.default',
      value: 'We collect',
    }),
    speechBubbleTwo: CmsShortTextSchema.default({
      key: 'HeroSection.speechBubbleTwo.default',
      value: 'We fix',
    }),
    speechBubbleThree: CmsShortTextSchema.default({
      key: 'HeroSection.speechBubbleThree.default',
      value: 'And we return your car',
    }),
    speechBubblePromo: CmsRichTextSchema.default({
      key: 'HeroSection.speechBubblePromo.default',
      content: {
        nodeType: BLOCKS.DOCUMENT,
        data: {},
        content: [
          {
            nodeType: BLOCKS.PARAGRAPH,
            data: {},
            content: [
              {
                data: {},
                marks: [],
                value: 'Psst... Free collection',
                nodeType: 'text',
              },
            ],
          },
          {
            nodeType: BLOCKS.PARAGRAPH,
            data: {},
            content: [
              {
                data: {},
                marks: [],
                value: '& delivery today!',
                nodeType: 'text',
              },
            ],
          },
        ],
      },
    }),
    googleReviewsStars: CmsNumberSchema.optional(),
    googleReviewsTotalReviews: CmsNumberSchema.optional(),
    googleReviewsLink: CmsShortTextSchema.optional(),
    googleReviewsReviewsText: CmsShortTextSchema.optional(),
    googleReviewsBasedOnText: CmsShortTextSchema.optional(),
    googleReviewsLogo: CmsImageSchema.optional(),
  }),
  configuration: z
    .object({
      reviewsProvider: z.nativeEnum(ReviewProvider).optional(),
      sku: z.string().optional(),
      preloadImages: z.boolean().optional(),
      lazyLoadImages: z.boolean().optional(),
    })
    .default(defaultConfiguration),
};

export type HeroSectionTranslatedEntities = SchemaType<(typeof HeroSectionSchema)['translatedEntities']>;
export type HeroSectionConfiguration = SchemaType<(typeof HeroSectionSchema)['configuration']>;
